import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme, Select, MenuItem, FormControl, Box} from "@mui/material";
import {formatCurrency} from "../utils/formatters";
import {useState} from "react";

const TableSection = ({data, columns, selectedCategory, onCategoryChange, isTopTable}) => {
    const theme = useTheme();

    if (!data || data.length === 0) {
        return (
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'left', mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={isTopTable ? `Top 10 ${selectedCategory}` : `Bottom 10 ${selectedCategory}`}
                            onChange={onCategoryChange}
                            displayEmpty
                            sx={{ 
                                fontSize: '1rem',
                                '& .MuiSelect-select': {
                                    padding: '4px 8px'
                                }
                            }}
                        >
                            <MenuItem value="products">Products</MenuItem>
                            <MenuItem value="customers">Customers</MenuItem>
                            <MenuItem value="salesReps">Sales Reps</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{textAlign: 'center', mt: 4}}>
                    No data available
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'left', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <Select
                        value={selectedCategory}
                        onChange={onCategoryChange}
                        displayEmpty
                        sx={{ 
                            fontSize: '1rem',
                            '& .MuiSelect-select': {
                                padding: '4px 8px'
                            }
                        }}
                    >
                        <MenuItem value="products">{ (isTopTable ? `Top 10 ` : `Bottom 10`) + ' Products'}</MenuItem>
                        <MenuItem value="customers">{ (isTopTable ? `Top 10 ` : `Bottom 10`) + ' Customers'}</MenuItem>
                        <MenuItem value="salesReps">{ (isTopTable ? `Top 10 ` : `Bottom 10`) + ' Sales Reps'}</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <TableContainer sx={{ height: 100 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((col, index) => (
                                <TableCell 
                                    key={col.field} 
                                    align={index === 0 ? 'left' : 'center'}
                                    style={{
                                        backgroundColor: theme.palette.grey[100],
                                        fontWeight: 'bold',
                                        fontSize: '0.75rem',
                                        border: '1px solid #ccc'
                                    }}
                                >
                                    {col.header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, index) => (
                            <TableRow key={index} hover>
                                {columns.map((col, colIndex) => (
                                    <TableCell 
                                        key={colIndex}
                                        align={colIndex === 0 ? 'left' : 'center'}
                                        style={{
                                            fontSize: '0.75rem',
                                            padding: '4px 8px',
                                            border: '1px solid #ccc'
                                        }}
                                    >
                                        {row[col.field]}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export const TopBottomPerformancePanel = ({detailData}) => {
    const theme = useTheme();
    const dims = theme.dimensions;
    const [topSelectedCategory, setTopSelectedCategory] = useState('products');
    const [bottomSelectedCategory, setBottomSelectedCategory] = useState('products');

    const handleTopCategoryChange = (event) => {
        setTopSelectedCategory(event.target.value);
    };

    const handleBottomCategoryChange = (event) => {
        setBottomSelectedCategory(event.target.value);
    };

    const transformData = (rawData, labelField) => {
        if (!rawData || rawData.length === 0) return [];

        return rawData.map(item => {
            const current = item.currentValue || 0;
            const prior = item.priorValue || 0;
            const delta = current - prior;

            return {
                label: item[labelField] || 'N/A',
                kpi: formatCurrency(current),
                target: formatCurrency(prior),
                delta: formatCurrency(delta),
                sortValue: current
            };
        });
    };

    const getTopBottom = (data) => {
        if (!data.length) return { top: [], bottom: [] };

        const sorted = [...data].sort((a, b) => b.sortValue - a.sortValue);

        return {
            top: sorted.slice(0, 10),
            bottom: sorted.slice(-10).reverse()
        };
    };

    const getCategoryData = (selectedCategory) => {
        switch(selectedCategory) {
            case 'customers':
                return {
                    rawData: detailData.customers,
                    labelField: "customerName",
                    columns: [
                        { header: "Account",        field: "label" },
                        { header: "Rev $",          field: "kpi" },
                        { header: "VM $",           field: "target" },
                        { header: "VM $ change",    field: "delta" }
                    ]
                };
            case 'products':
                return {
                    rawData: detailData.products,
                    labelField: "productCode", 
                    columns: [
                        { header: "Product/ SKU",   field: "label" },
                        { header: "Rev $",          field: "kpi" },
                        { header: "VM $",           field: "target" },
                        { header: "VM $ change",    field: "delta" }
                    ]
                };
            case 'salesReps':
                return {
                    rawData: detailData.salesReps,
                    labelField: "salesRepName",
                    columns: [
                        { header: "Sales Rep",      field: "label" },
                        { header: "Rev $",          field: "kpi" },
                        { header: "VM $",           field: "target" },
                        { header: "VM $ change",    field: "delta" }
                    ]
                };
            default:
                return {
                    rawData: [],
                    labelField: "",
                    columns: []
                };
        }
    };

    const topCategoryData = getCategoryData(topSelectedCategory);
    const bottomCategoryData = getCategoryData(bottomSelectedCategory);
    
    const topTransformedData = transformData(topCategoryData.rawData, topCategoryData.labelField);
    const bottomTransformedData = transformData(bottomCategoryData.rawData, bottomCategoryData.labelField);
    
    const topPerformers = getTopBottom(topTransformedData);
    const bottomPerformers = getTopBottom(bottomTransformedData);

    return (
        <div style={{ ...theme.palette.contentPanel, padding: 10, marginTop: 30, minHeight: dims.smallPanelHeight}}>
            <TableSection
                data={topPerformers.top}
                columns={topCategoryData.columns}
                selectedCategory={topSelectedCategory}
                onCategoryChange={handleTopCategoryChange}
                isTopTable={true}
            />
            <TableSection
                data={bottomPerformers.bottom}
                columns={bottomCategoryData.columns}
                selectedCategory={bottomSelectedCategory}
                onCategoryChange={handleBottomCategoryChange}
                isTopTable={false}
            />
        </div>
    );
};